import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { ShoppingCart, FileText, Gift } from "lucide-react";
import { Link } from 'wouter';
import { useState, useEffect } from 'react';

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

// Componente de contagem animada
function CountingNumber({ target, duration = 2000 }: { target: number; duration?: number }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrameId: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      setCount(Math.floor(target * progress));
      
      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, [target, duration]);

  return <span>{count}</span>;
}

export default function ProgramaFidelidade() {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1">
        {/* Hero */}
        <section className="relative h-[400px] md:h-[400px] flex items-center overflow-hidden" style={{backgroundImage: "url('/images/pages/hero-colheita.webp')", backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: "0 0 96px 0", boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)"}}>
          <div className="absolute inset-0" style={{backgroundColor: "rgba(0, 0, 0, 0.5)"}}></div>
          <div className="container relative z-10">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h1 className="text-3xl md:text-5xl mb-6" style={{fontFamily: "'Suez One'", color: '#ffffff'}}>
                Programa Fidelidade<br />Boa Colheita Produtores
              </h1>
              <p className="text-base md:text-xl" style={{lineHeight: "1.6", color: '#ffffff'}}>
                Agora, com o programa de relacionamento BOA COLHEITA PRODUTORES, toda compra realizada 
                conosco garante pontos para você trocar por produtos e serviços exclusivos.
              </p>
            </div>
          </div>
        </section>

        {/* Como funciona */}
        <section className="py-16 md:py-24 bg-background">
          <div className="container">
            <h2 className="text-4xl md:text-5xl mb-12 text-primary text-center" style={{fontFamily: "'Suez One'"}}>
              Como funciona?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Card 1 - Seiva */}
              <Card 
                className="h-full w-full bg-clip-padding backdrop-filter backdrop-blur-md bg-opacity-30  transition-all duration-300 hover:scale-105 cursor-pointer"
                style={{
                  backgroundColor: hoveredCard === 'step1' 
                    ? 'rgba(29, 134, 73, 0.35)' 
                    : 'rgba(29, 134, 73, 0.15)',
                  borderRadius: '12px',
                  border: '2px solid #1D8649'
                }}
                onMouseEnter={() => setHoveredCard('step1')}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{backgroundColor: '#1D8649'}}>
                    <div className="text-4xl font-bold" style={{fontFamily: "'Suez One'", color: '#f2f2f2'}}>1</div>
                  </div>
                  <h3 className="text-xl md:text-2xl font-semibold mb-3" style={{fontFamily: "'Suez One'", color: '#1D8649'}}>
                    Faça sua compra com a Fercci
                  </h3>
                  <p className="text-base text-muted-foreground" style={{lineHeight: "1.6"}}>
                    Adquira fertilizantes e produtos da linha Yara através da Fercci.
                  </p>
                </CardContent>
              </Card>

              {/* Card 2 - Aurora */}
              <Card 
                className="h-full w-full bg-clip-padding backdrop-filter backdrop-blur-md bg-opacity-30  transition-all duration-300 hover:scale-105 cursor-pointer"
                style={{
                  backgroundColor: hoveredCard === 'step2' 
                    ? 'rgba(211, 214, 85, 0.35)' 
                    : 'rgba(211, 214, 85, 0.15)',
                  borderRadius: '12px',
                  border: '2px solid #D3D655'
                }}
                onMouseEnter={() => setHoveredCard('step2')}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{backgroundColor: '#D3D655'}}>
                    <div className="text-4xl font-bold" style={{fontFamily: "'Suez One'", color: '#f2f2f2'}}>2</div>
                  </div>
                  <h3 className="text-xl md:text-2xl font-semibold mb-3" style={{fontFamily: "'Suez One'", color: '#D3D655'}}>
                    Cadastre sua Nota Fiscal na Orbia
                  </h3>
                  <p className="text-base text-muted-foreground" style={{lineHeight: "1.6"}}>
                    Registre sua nota fiscal na plataforma Orbia para acumular pontos.
                  </p>
                </CardContent>
              </Card>

              {/* Card 3 - Brisa */}
              <Card 
                className="h-full w-full bg-clip-padding backdrop-filter backdrop-blur-md bg-opacity-30  transition-all duration-300 hover:scale-105 cursor-pointer"
                style={{
                  backgroundColor: hoveredCard === 'step3' 
                    ? 'rgba(159, 207, 202, 0.35)' 
                    : 'rgba(159, 207, 202, 0.15)',
                  borderRadius: '12px',
                  border: '2px solid #9FCFCA'
                }}
                onMouseEnter={() => setHoveredCard('step3')}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{backgroundColor: '#9FCFCA'}}>
                    <div className="text-4xl font-bold" style={{fontFamily: "'Suez One'", color: '#f2f2f2'}}>3</div>
                  </div>
                  <h3 className="text-xl md:text-2xl font-semibold mb-3" style={{fontFamily: "'Suez One'", color: '#9FCFCA'}}>
                    Troque por Prêmios e serviços exclusivos
                  </h3>
                  <p className="text-base text-muted-foreground" style={{lineHeight: "1.6"}}>
                    Use seus pontos para resgatar prêmios e benefícios no catálogo.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Níveis de pontuação */}
        <section className="py-16 md:py-24" style={{backgroundColor: '#003B4A'}}>
          <div className="container">
            <h2 className="text-4xl md:text-5xl mb-12 text-white text-center" style={{fontFamily: "'Suez One'"}}>
              Níveis de pontuação
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Card 1 - Aurora */}
              <Card 
                className="h-full w-full bg-clip-padding backdrop-filter backdrop-blur-md bg-opacity-30  transition-all duration-300 hover:scale-105 cursor-pointer"
                style={{
                  backgroundColor: hoveredCard === 'nivel1' 
                    ? 'rgba(211, 214, 85, 0.25)' 
                    : 'rgba(211, 214, 85, 0.15)',
                  borderRadius: '12px',
                  border: '2px solid #D3D655'
                }}
                onMouseEnter={() => setHoveredCard('nivel1')}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <CardContent className="p-8 text-center">
                  <div className="mb-6">
                    <div className="text-5xl font-bold mb-2" style={{fontFamily: "'Suez One'", color: '#D3D655'}}><CountingNumber target={1} duration={1500} /></div>
                    <div className="text-sm" style={{color: '#D3D655'}}>ponto</div>
                  </div>
                  <div className="text-4xl font-bold mb-4" style={{fontFamily: "'Suez One'", color: '#D3D655'}}>
                    R$ <CountingNumber target={5} />
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{fontFamily: "'Suez One'", color: '#ffffff'}}>
                    Produtos YaraVita e YaraAmplix
                  </h3>
                  <p className="text-sm" style={{lineHeight: "1.6", color: '#ffffff'}}>
                    Ganhe 1 ponto a cada R$5 gastos em produtos especiais da linha YaraVita e YaraAmplix.
                  </p>
                </CardContent>
              </Card>

              {/* Card 2 - Colheita */}
              <Card 
                className="h-full w-full bg-clip-padding backdrop-filter backdrop-blur-md bg-opacity-30  transition-all duration-300 hover:scale-105 cursor-pointer"
                style={{
                  backgroundColor: hoveredCard === 'nivel2' 
                    ? 'rgba(199, 137, 62, 0.25)' 
                    : 'rgba(199, 137, 62, 0.15)',
                  borderRadius: '12px',
                  border: '2px solid #C7893E'
                }}
                onMouseEnter={() => setHoveredCard('nivel2')}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <CardContent className="p-8 text-center">
                  <div className="mb-6">
                    <div className="text-5xl font-bold mb-2" style={{fontFamily: "'Suez One'", color: '#C7893E'}}><CountingNumber target={1} duration={1500} /></div>
                    <div className="text-sm" style={{color: '#C7893E'}}>ponto</div>
                  </div>
                  <div className="text-4xl font-bold mb-4" style={{fontFamily: "'Suez One'", color: '#C7893E'}}>
                    R$ <CountingNumber target={15} />
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{fontFamily: "'Suez One'", color: '#ffffff'}}>
                    Produtos Premium
                  </h3>
                  <p className="text-sm" style={{lineHeight: "1.6", color: '#ffffff'}}>
                    Ganhe 1 ponto a cada R$15 gastos em produtos da linha Premium.
                  </p>
                </CardContent>
              </Card>

              {/* Card 3 - Brisa */}
              <Card 
                className="h-full w-full bg-clip-padding backdrop-filter backdrop-blur-md bg-opacity-30  transition-all duration-300 hover:scale-105 cursor-pointer"
                style={{
                  backgroundColor: hoveredCard === 'nivel3' 
                    ? 'rgba(159, 207, 202, 0.25)' 
                    : 'rgba(159, 207, 202, 0.15)',
                  borderRadius: '12px',
                  border: '2px solid #9FCFCA'
                }}
                onMouseEnter={() => setHoveredCard('nivel3')}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <CardContent className="p-8 text-center">
                  <div className="mb-6">
                    <div className="text-5xl font-bold mb-2" style={{fontFamily: "'Suez One'", color: '#9FCFCA'}}><CountingNumber target={1} duration={1500} /></div>
                    <div className="text-sm" style={{color: '#9FCFCA'}}>ponto</div>
                  </div>
                  <div className="text-4xl font-bold mb-4" style={{fontFamily: "'Suez One'", color: '#9FCFCA'}}>
                    R$ <CountingNumber target={500} />
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{fontFamily: "'Suez One'", color: '#ffffff'}}>
                    Produtos Diferenciados e Tradicionais
                  </h3>
                  <p className="text-sm" style={{lineHeight: "1.6", color: '#ffffff'}}>
                    Ganhe 1 ponto a cada R$500 gastos em produtos das linhas Diferenciados e Tradicionais.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Sobre a Orbia */}
        <section className="py-16 md:py-24 bg-background">
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl mb-6 text-primary" style={{fontFamily: "'Suez One'"}}>
                Sobre a Orbia
              </h2>
            </div>
            
            <Card className="h-full w-full bg-clip-padding backdrop-filter backdrop-blur-md bg-opacity-30 " style={{
              backgroundColor: 'rgba(159, 207, 202, 0.15)',
              borderRadius: '12px'
            }}>
              <CardContent className="p-8">
                <div className="flex flex-col items-center gap-6 text-center">
                  <div>
                    <img loading="lazy" 
                      src="/images/logo-orbia-transparente.png" 
                      alt="Orbia" 
                      className="h-16 w-auto mx-auto"
                    />
                  </div>
                  <div>
                    <p className="text-base md:text-lg text-muted-foreground mb-4" style={{lineHeight: "1.6"}}>
                      A <strong className="text-foreground">Orbia</strong> é a plataforma de tudo do 
                      Agronegócio e vem se consolidando como o principal marketplace agrícola do país, 
                      com mais de 240 distribuidores e cobertura nacional em insumos como defensivos, 
                      sementes e fertilizantes.
                    </p>
                    <p className="text-base md:text-lg text-muted-foreground" style={{lineHeight: "1.6"}}>
                      Através da parceria entre Fercci e Orbia, você tem acesso a um ecossistema completo 
                      de soluções para o campo, além de poder acumular e resgatar pontos de forma simples 
                      e transparente.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Vídeo Tutorial */}
        <section className="py-16 md:py-24 bg-background">
          <div className="container max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl mb-6 text-primary" style={{fontFamily: "'Suez One'"}}>
                Como Usar o Sistema
              </h2>
              <p className="text-lg text-muted-foreground" style={{lineHeight: "1.6"}}>
                Assista ao vídeo e aprenda passo a passo como utilizar o programa de fidelidade Boa Colheita Produtores e ganhar pontos em cada compra.
              </p>
            </div>
            
            <div className="relative w-full" style={{paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: '12px'}}>
              <iframe
                loading="lazy"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: '12px'
                }}
                src="https://www.youtube.com/embed/uoBI66DtF78"
                title="Como usar o Programa Boa Colheita Produtores"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-background">
          <div className="container max-w-3xl text-center">
            <h2 className="text-4xl md:text-5xl mb-6 text-primary" style={{fontFamily: "'Suez One'"}}>
              Pronto para começar a acumular pontos?
            </h2>
            <p className="text-lg text-muted-foreground mb-8" style={{lineHeight: "1.6"}}>
              Entre em contato conosco e saiba como participar do programa Boa Colheita Produtores.
            </p>
            <Link href="/contato" onClick={scrollToTop}>
              <button className="bg-accent hover:bg-accent/90 text-primary font-semibold px-8 py-4 rounded-md text-lg transition-all hover:scale-105 cursor-pointer uppercase">
                Quero participar
              </button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

